












































































import { mapState, mapGetters } from 'vuex';
import * as vr from '@/helpers/validation';
import { ValidationRule } from '@/api/schema';
import type { SelectItem } from '@/api/schema';
import {
  EDailyCheckInStreakDuration,
  EDailyCheckInStreakRestartType
} from '@/api/schema';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { getDailyCheckIn, updateDailyCheckIn } from '@/api/DailyCheckIn';

export default {
  name: 'DailyCheckInForm',
  data(): Record<string, unknown> {
    return {
      isLoading: false,
      dailyCheckIn: null,
      formData: {
        firstReward: '',
        rewardStep: '',
        streakBonus: '',
        streakDuration: '',
        streakRestartType: ''
      },
      isFormValid: false
    };
  },
  computed: {
    ...mapState('Auth', ['tokenSymbol']),
    ...mapGetters('Onboarding', ['operatorId']),
    isFormDisabled(): boolean {
      return this.isLoading;
    },
    isSaveButtonDisabled(): boolean {
      return this.isFormDisabled || !this.isFormValid;
    },
    formRules(): Record<string, ValidationRule[]> {
      return {
        firstReward: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        rewardStep: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        streakBonus: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        streakDuration: [vr.required],
        streakRestartType: [vr.required]
      };
    },
    streakDurations(): SelectItem[] {
      return [
        {
          text: '7 days',
          value: EDailyCheckInStreakDuration.SEVEN
        },
        {
          text: '14 days',
          value: EDailyCheckInStreakDuration.FOURTEEN
        }
      ];
    },
    streakRestartTypes(): SelectItem[] {
      return [
        {
          text: 'One streak per month',
          value: EDailyCheckInStreakRestartType.ONE_PER_MONTH
        },
        {
          text: 'After reaching the max streak',
          value: EDailyCheckInStreakRestartType.REACHING_MAX
        }
      ];
    }
  },
  watch: {
    operatorId: {
      handler(): void {
        if (!this.operatorId) {
          return;
        }

        this.loadDailyCheckIn();
      },
      immediate: true
    },
    dailyCheckIn: {
      handler(): void {
        if (this.dailyCheckIn === null) {
          return;
        }

        this.setupFormData();

        this.$refs.form?.resetValidation();
      },
      deep: true
    }
  },
  methods: {
    setupFormData(): void {
      if (this.dailyCheckIn === null) {
        return;
      }

      this.formData = {
        firstReward: this.dailyCheckIn.firstReward
          ? `${this.dailyCheckIn.firstReward}`
          : '',
        rewardStep: this.dailyCheckIn.rewardStep
          ? `${this.dailyCheckIn.rewardStep}`
          : '',
        streakBonus: this.dailyCheckIn.streakBonus
          ? `${this.dailyCheckIn.streakBonus}`
          : '',
        streakDuration: this.dailyCheckIn.streakDuration
          ? `${this.dailyCheckIn.streakDuration}`
          : '',
        streakRestartType: this.dailyCheckIn.streakRestartType
          ? `${this.dailyCheckIn.streakRestartType}`
          : ''
      };
    },
    resetComponentState(): void {
      this.isLoading = true;
      this.dailyCheckIn = null;
      this.formData = {
        firstReward: '',
        rewardStep: '',
        streakBonus: '',
        streakDuration: '',
        streakRestartType: ''
      };
      this.isFormValid = false;
      this.$refs.form?.resetValidation();
    },
    async loadDailyCheckIn(): Promise<void> {
      if (!this.operatorId) {
        return;
      }

      try {
        this.resetComponentState();
        this.dailyCheckIn = await getDailyCheckIn(this.operatorId);
        this.isLoading = false;
      } catch (error) {
        this.$toast.error('Something was wrong', error);
      }
    },
    async submitForm(): Promise<void> {
      if (!this.operatorId || this.isFormDisabled) {
        return;
      }

      this.$refs.form.validate();

      await this.$nextTick();

      if (!this.isFormValid) {
        return;
      }

      this.isLoading = true;

      try {
        this.dailyCheckIn = await updateDailyCheckIn(
          this.operatorId,
          this.formData
        );
        this.$toast.success('Daily Check-in was successfully updated!');
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.isLoading = false;
      }
    },
    handleSubmitForm(): void {
      this.submitForm();
    }
  }
};
